// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Financeiros/ConciliacaoOfx.tsx
//  Conciliação bancária via import de extrato OFX.
// ════════════════════════════════════════════════════════════════════════════

import { useState, useCallback, useEffect } from 'react';
import { Upload, RefreshCw, CheckCircle2, X, Loader2, FileSpreadsheet, Banknote, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { usePaginacaoLocal } from '@/hooks/usePaginacaoLocal';
import { usePaginacaoServidor } from '@/hooks/usePaginacaoServidor';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';
import { conciliacaoOfxService, type MovimentoBancario, type SugestaoCasamento, type ImportOfxRegistro } from '@/features/financeiro/services/conciliacaoOfxService';
import { cadastrosFinanceirosService } from '@/features/financeiro/services/cadastrosFinanceirosService';
import { getApiErrorMessage } from '@/lib/apiClient';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtCur = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString('pt-BR');

// Tabela com altura máxima: rola por dentro (cabeçalho fixo) em vez de esticar a página.
// [&>div]:overflow-visible neutraliza o wrapper overflow-auto do <Table>, senão o sticky não pega.
const TABELA_ROLAVEL = 'border rounded-lg overflow-auto max-h-[60vh] [&>div]:overflow-visible';
const CABECALHO_FIXO = 'sticky top-0 z-10 bg-card';

// ── Component ────────────────────────────────────────────────────────────────

export default function ConciliacaoOfx() {
  const { toast } = useToast();
  const [contas, setContas] = useState<{ id: number; descricao: string }[]>([]);
  const [contaId, setContaId] = useState<number | null>(null);

  const [arquivo, setArquivo] = useState<File | null>(null);
  const [importando, setImportando] = useState(false);

  const [movimentos, setMovimentos] = useState<MovimentoBancario[]>([]);
  const [movPaginacao, setMovPaginacao] = useState<{ total: number; totalPaginas: number } | null>(null);
  // Contadores do topo: totais reais da conta (independem do filtro e da página).
  const [contagem, setContagem] = useState({ conciliados: 0, pendentes: 0 });
  const [sugestoes, setSugestoes] = useState<SugestaoCasamento[]>([]);
  const [imports, setImports] = useState<ImportOfxRegistro[]>([]);
  const [filtroConciliado, setFiltroConciliado] = useState<'todos' | 'pendentes' | 'conciliados'>('pendentes');
  const [carregando, setCarregando] = useState(false);
  const [casandoId, setCasandoId] = useState<number | null>(null);

  // ── Carregar contas correntes ──────────────────────────────────────────────

  useEffect(() => {
    cadastrosFinanceirosService.getContasCorrentesCombo()
      .then((lista) => {
        setContas(lista as unknown as { id: number; descricao: string }[]);
        if (lista.length > 0 && contaId === null) {
          const first = lista[0] as unknown as { id: number };
          setContaId(first.id);
        }
      })
      .catch(() => toast({ title: 'Erro ao carregar contas correntes', variant: 'destructive' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Carregar movimentos / sugestoes / imports ──────────────────────────────

  const pagMov = usePaginacaoServidor({ resetarQuando: [contaId, filtroConciliado] });
  const { pagina: paginaMov, tamanhoPagina: tamanhoMov } = pagMov;

  const carregarTudo = useCallback(async () => {
    if (!contaId) return;
    setCarregando(true);
    try {
      const [movResp, concResp, pendResp, sugResp, impResp] = await Promise.all([
        conciliacaoOfxService.listarMovimentos(contaId, {
          conciliado: filtroConciliado === 'todos' ? undefined :
                      filtroConciliado === 'conciliados' ? true : false,
          pagina: paginaMov, tamanhoPagina: tamanhoMov,
        }),
        conciliacaoOfxService.listarMovimentos(contaId, { conciliado: true, pagina: 1, tamanhoPagina: 1 }),
        conciliacaoOfxService.listarMovimentos(contaId, { conciliado: false, pagina: 1, tamanhoPagina: 1 }),
        conciliacaoOfxService.sugestoes(contaId),
        conciliacaoOfxService.historicoImports(contaId),
      ]);
      setMovimentos(movResp.itens);
      setMovPaginacao({ total: movResp.total, totalPaginas: movResp.totalPaginas });
      setContagem({ conciliados: concResp.total, pendentes: pendResp.total });
      setSugestoes(sugResp);
      setImports(impResp);
    } catch (e) {
      toast({ title: 'Erro ao carregar dados', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setCarregando(false);
    }
  }, [contaId, filtroConciliado, paginaMov, tamanhoMov, toast]);

  const pagSug = usePaginacaoLocal(sugestoes);
  const pagImp = usePaginacaoLocal(imports);

  useEffect(() => {
    carregarTudo();
  }, [carregarTudo]);

  // ── Importar arquivo OFX ───────────────────────────────────────────────────

  const handleImportar = useCallback(async () => {
    if (!arquivo || !contaId) return;
    setImportando(true);
    try {
      const result = await conciliacaoOfxService.importar(contaId, arquivo);
      toast({
        title: 'Import concluído',
        description: `${result.qtdImportados} movimentos importados, ${result.qtdDuplicados} duplicados ignorados.`,
      });
      setArquivo(null);
      await carregarTudo();
    } catch (e) {
      toast({ title: 'Erro no import', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setImportando(false);
    }
  }, [arquivo, contaId, carregarTudo, toast]);

  // ── Confirmar casamento (sugestão automática) ──────────────────────────────

  const handleCasar = useCallback(async (sug: SugestaoCasamento) => {
    setCasandoId(sug.movimentoBancarioId);
    try {
      await conciliacaoOfxService.casar(sug.movimentoBancarioId, sug.tipoSugerido, sug.contaSugeridaId);
      toast({ title: 'Casamento confirmado.' });
      await carregarTudo();
    } catch (e) {
      toast({ title: 'Erro ao casar', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setCasandoId(null);
    }
  }, [carregarTudo, toast]);

  // ── Desfazer casamento ─────────────────────────────────────────────────────

  const handleDesfazer = useCallback(async (id: number) => {
    setCasandoId(id);
    try {
      await conciliacaoOfxService.desfazer(id);
      toast({ title: 'Casamento desfeito.' });
      await carregarTudo();
    } catch (e) {
      toast({ title: 'Erro ao desfazer', description: getApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setCasandoId(null);
    }
  }, [carregarTudo, toast]);

  // ── Render ─────────────────────────────────────────────────────────────────

  const totalConciliado = contagem.conciliados;
  const totalPendente = contagem.pendentes;
  const totalMovimentos = totalConciliado + totalPendente;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Banknote className="h-8 w-8" />
          Conciliação Bancária OFX
        </h1>
        <p className="text-muted-foreground mt-1">
          Importe extratos OFX do banco e concilie automaticamente com Contas a Receber e Pagar.
        </p>
      </div>

      {/* Topo: seletor de conta + import */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label>Conta Corrente</Label>
              <Select
                value={contaId?.toString() ?? ''}
                onValueChange={(v) => setContaId(Number(v))}
              >
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {contas.map((c) => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.descricao}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Arquivo OFX</Label>
              <Input
                type="file"
                accept=".ofx,.OFX"
                onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                disabled={importando}
              />
            </div>

            <Button
              onClick={handleImportar}
              disabled={!arquivo || !contaId || importando}
              className="w-full md:w-auto"
            >
              {importando
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Importando...</>
                : <><Upload className="h-4 w-4 mr-2" />Importar Extrato</>}
            </Button>
          </div>

          <div className="flex flex-wrap gap-3 text-sm pt-2 border-t">
            <Badge variant="outline" className="gap-1">
              <FileSpreadsheet className="h-3 w-3" /> Total: {totalMovimentos}
            </Badge>
            <Badge variant="default" className="gap-1 bg-green-600">
              <CheckCircle2 className="h-3 w-3" /> Conciliados: {totalConciliado}
            </Badge>
            <Badge variant="secondary" className="gap-1">
              Pendentes: {totalPendente}
            </Badge>
            <Badge variant="outline" className="gap-1">
              <Sparkles className="h-3 w-3" /> Sugestões: {sugestoes.length}
            </Badge>
            <Button variant="ghost" size="sm" onClick={carregarTudo} disabled={carregando}>
              <RefreshCw className={`h-3 w-3 mr-1 ${carregando ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Sugestões / Movimentos / Histórico */}
      <Tabs defaultValue="sugestoes">
        <TabsList>
          <TabsTrigger value="sugestoes" className="gap-2">
            <Sparkles className="h-4 w-4" />Sugestões ({sugestoes.length})
          </TabsTrigger>
          <TabsTrigger value="movimentos" className="gap-2">
            <FileSpreadsheet className="h-4 w-4" />Movimentos ({movPaginacao?.total ?? 0})
          </TabsTrigger>
          <TabsTrigger value="historico" className="gap-2">
            <Upload className="h-4 w-4" />Histórico ({imports.length})
          </TabsTrigger>
        </TabsList>

        {/* Sugestões */}
        <TabsContent value="sugestoes">
          <Card>
            <CardHeader><CardTitle className="text-base">Casamentos sugeridos automaticamente</CardTitle></CardHeader>
            <CardContent>
              {sugestoes.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Nenhuma sugestão de casamento encontrada.
                </p>
              ) : (
                <div className="space-y-3">
                <div className={TABELA_ROLAVEL}>
                  <Table>
                    <TableHeader className={CABECALHO_FIXO}>
                      <TableRow>
                        <TableHead>Data Mov.</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Memo</TableHead>
                        <TableHead>Sugere</TableHead>
                        <TableHead>Documento</TableHead>
                        <TableHead>Pessoa</TableHead>
                        <TableHead>Vencimento</TableHead>
                        <TableHead className="text-center">Score</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagSug.itensPaginados.map((s) => (
                        <TableRow key={s.movimentoBancarioId}>
                          <TableCell>{fmtDate(s.dtMovimento)}</TableCell>
                          <TableCell className={`text-right tabular-nums font-semibold ${s.valor >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {fmtCur(s.valor)}
                          </TableCell>
                          <TableCell className="max-w-xs truncate text-sm">{s.memo}</TableCell>
                          <TableCell>
                            <Badge variant={s.tipoSugerido === 'CR' ? 'default' : 'destructive'}>
                              {s.tipoSugerido === 'CR' ? 'Receber' : 'Pagar'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{s.contaSugeridaDoc}</TableCell>
                          <TableCell className="max-w-xs truncate">{s.contaSugeridaPessoa}</TableCell>
                          <TableCell>{fmtDate(s.contaSugeridaVencimento)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant={s.scoreCasamento >= 80 ? 'default' : 'secondary'}>
                              {s.scoreCasamento}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              onClick={() => handleCasar(s)}
                              disabled={casandoId === s.movimentoBancarioId}
                            >
                              {casandoId === s.movimentoBancarioId
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : <CheckCircle2 className="h-3 w-3" />}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <PaginacaoRodape
                  pagina={pagSug.pagina} totalPaginas={pagSug.totalPaginas} totalItens={pagSug.totalItens}
                  primeiroItem={pagSug.primeiroItem} ultimoItem={pagSug.ultimoItem}
                  tamanhoPagina={pagSug.tamanhoPagina}
                  onPaginaChange={pagSug.setPagina} onTamanhoPaginaChange={pagSug.setTamanhoPagina}
                />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Movimentos */}
        <TabsContent value="movimentos">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-base">Movimentos do Extrato</CardTitle>
                <Select value={filtroConciliado} onValueChange={(v: 'todos' | 'pendentes' | 'conciliados') => setFiltroConciliado(v)}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendentes">Pendentes</SelectItem>
                    <SelectItem value="conciliados">Conciliados</SelectItem>
                    <SelectItem value="todos">Todos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {movimentos.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {carregando ? 'Carregando...' : 'Nenhum movimento encontrado.'}
                </p>
              ) : (
                <div className="space-y-3">
                <div className={TABELA_ROLAVEL}>
                  <Table>
                    <TableHeader className={CABECALHO_FIXO}>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Tipo OFX</TableHead>
                        <TableHead>Memo</TableHead>
                        <TableHead>Doc.</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movimentos.map((m) => (
                        <TableRow key={m.id}>
                          <TableCell>{fmtDate(m.dtMovimento)}</TableCell>
                          <TableCell className={`text-right tabular-nums font-semibold ${m.valor >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {fmtCur(m.valor)}
                          </TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{m.tipoOfx}</Badge></TableCell>
                          <TableCell className="max-w-md truncate text-sm">{m.memo}</TableCell>
                          <TableCell className="font-mono text-sm">{m.checkNum ?? '—'}</TableCell>
                          <TableCell className="text-center">
                            {m.isConciliado ? (
                              <Badge variant="default" className="gap-1 bg-green-600">
                                <CheckCircle2 className="h-3 w-3" />
                                {m.conciliadoComTipo} #{m.conciliadoComId}
                              </Badge>
                            ) : (
                              <Badge variant="secondary">Pendente</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {m.isConciliado && (
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => handleDesfazer(m.id)}
                                disabled={casandoId === m.id}
                                title="Desfazer conciliação"
                              >
                                {casandoId === m.id
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : <X className="h-3 w-3" />}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <PaginacaoRodape {...pagMov.rodape(movPaginacao)} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Histórico de imports */}
        <TabsContent value="historico">
          <Card>
            <CardHeader><CardTitle className="text-base">Histórico de Imports OFX</CardTitle></CardHeader>
            <CardContent>
              {imports.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Nenhum import realizado.</p>
              ) : (
                <div className="space-y-3">
                <div className={TABELA_ROLAVEL}>
                  <Table>
                    <TableHeader className={CABECALHO_FIXO}>
                      <TableRow>
                        <TableHead>Data Import</TableHead>
                        <TableHead>Arquivo</TableHead>
                        <TableHead>Período</TableHead>
                        <TableHead className="text-center">Importados</TableHead>
                        <TableHead className="text-center">Duplicados</TableHead>
                        <TableHead className="text-right">Saldo Final</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pagImp.itensPaginados.map((i) => (
                        <TableRow key={i.id}>
                          <TableCell>{new Date(i.dtImport).toLocaleString('pt-BR')}</TableCell>
                          <TableCell className="font-mono text-sm">{i.nomeArquivo}</TableCell>
                          <TableCell className="text-sm">{fmtDate(i.dtInicio)} → {fmtDate(i.dtFim)}</TableCell>
                          <TableCell className="text-center font-semibold text-green-600">{i.qtdMovimentos}</TableCell>
                          <TableCell className="text-center text-muted-foreground">{i.qtdDuplicados}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {i.saldoFinal !== null ? fmtCur(i.saldoFinal) : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <PaginacaoRodape
                  pagina={pagImp.pagina} totalPaginas={pagImp.totalPaginas} totalItens={pagImp.totalItens}
                  primeiroItem={pagImp.primeiroItem} ultimoItem={pagImp.ultimoItem}
                  tamanhoPagina={pagImp.tamanhoPagina}
                  onPaginaChange={pagImp.setPagina} onTamanhoPaginaChange={pagImp.setTamanhoPagina}
                />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
